/**
 * Route configuration mapping tab keys to URL paths.
 * This provides a single source of truth for all route definitions.
 */

export type TabKey =
  | 'dashboard'
  | 'music'
  | 'video'
  | 'image'
  | 'banner'
  | 'category'
  | 'user'
  | 'domain'
  | 'heroSection'
  | 'aboutSection'
  | 'infoKit'
  | 'awardsSection'
  | 'awardAssets'
  | 'contactWebsite'
  | 'contactSection'
  | 'siteBranding'
  | 'toursEvents'
  | 'serverImages'
  | 'masterTheme'
  | 'masterPlatforms'
  | 'masterCategory'
  | 'platforms'
  | 'onboarding'
  | 'profile'
  | 'billing'
  | 'organizations'
  | 'organizationDetail'
  | 'managerAccess'
  // Management-only website sections
  | 'services'
  | 'artistRoster'
  | 'testimonials'
  | 'clients'
  | 'team';

interface RouteConfig {
  path: string;
  tab: TabKey;
}

export const routes: RouteConfig[] = [
  { path: '/', tab: 'dashboard' },
  { path: '/showcase', tab: 'music' },
  { path: '/videos', tab: 'video' },
  { path: '/images', tab: 'image' },
  { path: '/banner', tab: 'banner' },
  { path: '/categories', tab: 'category' },
  { path: '/users', tab: 'user' },
  { path: '/domains', tab: 'domain' },
  { path: '/hero-section', tab: 'heroSection' },
  { path: '/about-section', tab: 'aboutSection' },
  { path: '/info-kit', tab: 'infoKit' },
  { path: '/awards-section', tab: 'awardsSection' },
  { path: '/award-assets', tab: 'awardAssets' },
  { path: '/contact-website', tab: 'contactWebsite' },
  { path: '/contact-section', tab: 'contactSection' },
  { path: '/site-branding', tab: 'siteBranding' },
  { path: '/tours-events', tab: 'toursEvents' },
  { path: '/server-images', tab: 'serverImages' },
  { path: '/master-themes', tab: 'masterTheme' },
  { path: '/master-platforms', tab: 'masterPlatforms' },
  { path: '/master-categories', tab: 'masterCategory' },
  { path: '/platforms', tab: 'platforms' },
  { path: '/onboarding', tab: 'onboarding' },
  { path: '/profile', tab: 'profile' },
  { path: '/billing', tab: 'billing' },
  { path: '/organizations', tab: 'organizations' },
  { path: '/organizations/:id', tab: 'organizationDetail' },
  { path: '/manager-access', tab: 'managerAccess' },
  // Management-only website sections
  { path: '/services', tab: 'services' },
  { path: '/artist-roster', tab: 'artistRoster' },
  { path: '/testimonials', tab: 'testimonials' },
  { path: '/clients', tab: 'clients' },
  { path: '/team', tab: 'team' },
];

/** Get the URL path for a given tab key */
export function getPathForTab(tab: TabKey | string): string {
  const route = routes.find(r => r.tab === tab);
  return route?.path ?? '/';
}

/** Get the tab key for a given URL path */
export function getTabForPath(path: string): TabKey {
  // Direct match first.
  const exact = routes.find(r => r.path === path);
  if (exact) return exact.tab;
  // Param-aware match: /organizations/abc123 → /organizations/:id
  for (const r of routes) {
    if (!r.path.includes(':')) continue;
    const pattern = new RegExp(
      '^' + r.path.replace(/:[^/]+/g, '[^/]+') + '$'
    );
    if (pattern.test(path)) return r.tab;
  }
  return 'dashboard';
}
